import React, { useState, useRef, useEffect } from 'react';
import { Checkbox } from 'carbon-components-react';
import './MultiSelectDropdown.scss';

const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  label,
  placeholder = 'Select options...',
  selectAllLabel = 'Select All',
  idKey = 'id',
  labelKey = 'description',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleToggle = (value) => {
    const stringValue = String(value);
    const next = selectedValues.includes(stringValue)
      ? selectedValues.filter((v) => v !== stringValue)
      : [...selectedValues, stringValue];
    onChange(next);
  };

  const handleSelectAll = () => {
    const allIds = options.map((o) => String(o[idKey]));
    const allSelected =
      options.length > 0 && selectedValues.length === options.length;
    onChange(allSelected ? [] : allIds);
  };

  const allSelected =
    options.length > 0 && selectedValues.length === options.length;

  const selectedLabels = options
    .filter((o) => selectedValues.includes(String(o[idKey])))
    .map((o) => o[labelKey]);

  const triggerText =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels.length} selected`;

  return (
    <div className="multi-select-dropdown" ref={containerRef}>
      {label && <label className="bx--label">{label}</label>}
      <button
        type="button"
        className={`multi-select-dropdown__trigger ${isOpen ? 'multi-select-dropdown__trigger--open' : ''}`}
        onClick={handleTriggerClick}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="multi-select-dropdown__trigger-text">{triggerText}</span>
        <span className="multi-select-dropdown__trigger-icon">
          <ChevronDownIcon />
        </span>
      </button>

      {isOpen && (
        <div className="multi-select-dropdown__menu" role="listbox" aria-multiselectable="true">
          <div className="multi-select-dropdown__menu-inner">
            {options.length > 0 && (
              <div className="multi-select-dropdown__item">
                <Checkbox
                  id="multi-select-select-all"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  labelText={selectAllLabel}
                />
              </div>
            )}
            {options.map((option) => {
              const value = String(option[idKey]);
              const checked = selectedValues.includes(value);
              return (
                <div key={value} className="multi-select-dropdown__item">
                  <Checkbox
                    id={`multi-select-${value}`}
                    checked={checked}
                    onChange={() => handleToggle(value)}
                    labelText={option[labelKey]}
                  />
                </div>
              );
            })}
            {options.length === 0 && (
              <div className="multi-select-dropdown__empty">No options available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
